import { AppNode } from '../types';
import type { Edge } from '../types';
import { ExpressionEngine } from '../utils/expressionEngine';

export interface ExecutionResult {
  nextNodes: string[]; // Changed from single ID to array for parallel support
  logs: string[];
  traversedEdgeIds: string[]; // Track which edges were used
}

/**
 * Determines the next node(s) in the workflow based on the current node type, edges, and action data.
 * `_variables` is reserved for future expression evaluation inside flow-choice logic; callers pass
 * the current simulation variable map so the signature stays stable.
 */
export const determineNextNode = (
  node: AppNode,
  edges: Edge[],
  actionData: any,
  _variables: any
): ExecutionResult => {
  const outgoingEdges = edges.filter(e => e.source === node.id);
  
  // Check for explicit boundary trigger (takes precedence over standard flow)
  if (actionData && actionData.triggerBoundaryId) {
      const boundaryEdge = edges.find(e => 
          e.source === node.id && 
          e.sourceHandle === 'boundary' && 
          e.target === actionData.triggerBoundaryId
      );
      
      if (boundaryEdge) {
          return { 
              nextNodes: [boundaryEdge.target], 
              logs: [`Boundary event triggered: ${actionData.boundaryLabel || 'Interruption'}`],
              traversedEdgeIds: [boundaryEdge.id] 
          };
      }
  }

  if (outgoingEdges.length === 0) {
      return { nextNodes: [], logs: ['End of flow reached.'], traversedEdgeIds: [] };
  }

  // Strategy Pattern based on Node Type
  switch(node.type) {
    case 'XorGateway':
      return handleXorGateway(node, outgoingEdges, actionData);
    case 'InclusiveGateway':
      return handleInclusiveGateway(node, outgoingEdges, actionData);
    case 'ParallelGateway':
      return handleParallelGateway(node, outgoingEdges, actionData);
    case 'UserTask':
      return handleUserTask(node, outgoingEdges, actionData);
    default: {
      // Default: Follow first edge (Sequence Flow)
      // Exclude boundary edges for standard "Next" clicks
      const standardEdges = outgoingEdges.filter((e: { sourceHandle?: string | null }) => e.sourceHandle !== 'boundary');
      if (standardEdges.length > 0) {
          return {
              nextNodes: [standardEdges[0].target],
              logs: [],
              traversedEdgeIds: [standardEdges[0].id]
          };
      }
      return { nextNodes: [], logs: ['No standard outgoing path found.'], traversedEdgeIds: [] };
    }
  }
};

/**
 * Pre-evaluates node logic (e.g. gateway conditions) when entering a node.
 * Returns suggested data (e.g. path index).
 */
export const evaluateNode = (node: AppNode, variables: any): string | null => {
     if (node.type === 'XorGateway') {
          const conditions = node.data.conditions || [];
          let matchIndex = -1;
          
          for (const cond of conditions) {
              if (cond.expression) {
                  if (ExpressionEngine.evaluateCondition(cond.expression, variables)) {
                      matchIndex = cond.path;
                      break;
                  }
              } else if (matchIndex === -1) {
                   // Capture default path (empty expression) if seen, but keep looking for explicit matches
                   matchIndex = cond.path; 
              }
          }
          // Fallback to last path if defined and nothing matched
          if (matchIndex === -1 && conditions.length > 0) {
              matchIndex = conditions[conditions.length - 1].path;
          }
          
          return String(matchIndex);
     }
     return null;
}

// --- Strategy Implementations ---

const handleXorGateway = (node: AppNode, outgoingEdges: Edge[], actionData: any): ExecutionResult => {
  const selectedPathIndex = actionData?.selectedPath;
  
  // Filter out boundary edges just in case
  const validEdges = outgoingEdges.filter(e => e.sourceHandle !== 'boundary');

  // Strategy 1: Match by Label (defined in outputs)
  // This is the robust way: The edge label must match the selected path name.
  if (node.data.outputs && node.data.outputs[selectedPathIndex]) {
      const targetLabel = node.data.outputs[selectedPathIndex];
      // Note: Edge labels might be undefined if not set
      const edge = validEdges.find(e => e.label === targetLabel);
      if (edge) {
          return { 
              nextNodes: [edge.target], 
              logs: [`Gateway decision: ${targetLabel}`],
              traversedEdgeIds: [edge.id]
          };
      }
  }

  // Strategy 2: Match by Index (Fallback)
  // If labels aren't strictly set or don't match, we assume the visual order (array order) matches the logical order.
  const edgeByIndex = validEdges[selectedPathIndex];
  if (edgeByIndex) {
       return { 
          nextNodes: [edgeByIndex.target], 
          logs: [`Gateway decision: Path ${selectedPathIndex + 1} (Matched by Index)`],
          traversedEdgeIds: [edgeByIndex.id]
      };
  }

  return { nextNodes: [], logs: [`Error: Could not find outgoing connection for Path ${selectedPathIndex + 1}. Ensure the connection is labeled "${node.data.outputs?.[selectedPathIndex]}" or connected in order.`], traversedEdgeIds: [] };
};

const handleInclusiveGateway = (node: AppNode, outgoingEdges: Edge[], actionData: any): ExecutionResult => {
    // In this simulator UI, we currently treat Inclusive similar to Xor (single path selection).
    const selectedPathIndex = actionData?.selectedPath;
    const validEdges = outgoingEdges.filter(e => e.sourceHandle !== 'boundary');
    
    if (selectedPathIndex !== undefined) {
         // Try Label Match
         if (node.data.outputs && node.data.outputs[selectedPathIndex]) {
             const targetLabel = node.data.outputs[selectedPathIndex];
             const edge = validEdges.find(e => e.label === targetLabel);
             if (edge) {
                 return { 
                     nextNodes: [edge.target], 
                     logs: [`Inclusive Gateway: ${targetLabel}`],
                     traversedEdgeIds: [edge.id]
                 };
             }
         }

         // Try Index Match
         const edge = validEdges[selectedPathIndex];
         if (edge) return { 
             nextNodes: [edge.target], 
             logs: [`Inclusive Gateway: Path ${selectedPathIndex + 1}`],
             traversedEdgeIds: [edge.id]
         };
    }

    // Fallback: Default to first
    if (validEdges.length > 0) {
        return { 
            nextNodes: [validEdges[0].target], 
            logs: ['Inclusive Gateway: Defaulting to first path'],
            traversedEdgeIds: [validEdges[0].id]
        };
    }
    return { nextNodes: [], logs: ['No path found.'], traversedEdgeIds: [] };
};

const handleParallelGateway = (node: AppNode, outgoingEdges: Edge[], _actionData: any): ExecutionResult => {
    // If it's a merging gateway, we just continue to the next single output
    if (node.data.isMerging) {
        // Filter out boundary edges if any exist (rare for gateways but possible in loose modeling)
        const validEdges = outgoingEdges.filter(e => e.sourceHandle !== 'boundary');
        if (validEdges.length > 0) {
             return { 
                nextNodes: [validEdges[0].target], 
                logs: ['Parallel Merge: Proceeding.'],
                traversedEdgeIds: [validEdges[0].id]
            };
        }
        return { nextNodes: [], logs: ['Parallel Merge: No outgoing path.'], traversedEdgeIds: [] };
    }

    // Split: Activate ALL outgoing edges (excluding boundary associations)
    const standardEdges = outgoingEdges.filter(e => e.sourceHandle !== 'boundary');
    const targets = standardEdges.map(e => e.target);
    const edgeIds = standardEdges.map(e => e.id);
    
    return { 
        nextNodes: targets, 
        logs: [`Parallel Split: Activating ${targets.length} paths.`],
        traversedEdgeIds: edgeIds
    };
}

const handleUserTask = (_node: AppNode, outgoingEdges: Edge[], _actionData: any): ExecutionResult => {
    // Standard flow - find any non-boundary edge
    const nextEdge = outgoingEdges.find(e => e.sourceHandle !== 'boundary');
    
    if (nextEdge) {
        return { 
            nextNodes: [nextEdge.target], 
            logs: ['User task completed.'],
            traversedEdgeIds: [nextEdge.id]
        };
    }
    return { nextNodes: [], logs: ['No outgoing path.'], traversedEdgeIds: [] };
};